import * as React from "react";
import {
  Box,
  Paper,
  Button,
  Grid,
  Backdrop,
  TableContainer,
  Table,
  TableCell,
  TableBody,
  TableRow,
  TableHead,
  Typography,
  TextField,
  MenuItem,
  IconButton,
  Tooltip,
} from "@mui/material";
import { styled, alpha } from "@mui/material/styles";
import Menu, { MenuProps } from "@mui/material/Menu";
import { useState, useEffect, useContext, useMemo } from "react";
import Title from "../../Components/Title";
import ShortcutButton from "../../Components/ShortcutButton";
import { PreferencesContext } from "../../PreferencesContext";
import { AlertType } from "../../shared/Modal/AlertType";
import MissingConsumer from "../../Components/MissingConsumer";
import DownloadIcon from "@mui/icons-material/Download";
import LocalPrintshopIcon from "@mui/icons-material/LocalPrintshop";
import { KeyboardArrowDownSharp } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { format } from "date-fns";
import SplashComponent from "../../Navigation/splash";
import { GetProductList } from "../../Services/CategoryProductService";
import { GetWarehouseList } from "../../Services/WarehouseService";
import { GetCategoryList } from "../../Services/Category";
import MultiSelectSim from "../../Components/MultiSelectSimple";
import { EncryptDecryptService } from "../../Services/common/EncryptionService";
import { useLocation, useNavigate } from "react-router-dom";
import CustomDateRangePicker from "../../Components/CustomDateRange";
import { GetStockLedgerList } from "../../Services/StockService";
import {
  StockLedgerProductGroup,
  StockLedgerDetailRow,
  VOUCHER_TYPE_OPTIONS,
  VALUATION_OPTIONS,
  buildProductGroups,
  formatLedgerDate,
  qtyFmt,
  amtFmt,
  exportLedgerToCsvOrExcel,
  printLedger,
} from "./stockLedgerUtils";
import { LV_COLORS } from "./LedgerVoucher/ledgerVoucherUi";

interface IProduct {
  productId: number;
  name: string;
  label: string;
  value: string;
  categoryId?: number;
}

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
    transformOrigin={{ vertical: "top", horizontal: "right" }}
    {...props}
  />
))(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 120,
    color:
      theme.palette.mode === "light"
        ? "rgb(55, 65, 81)"
        : theme.palette.grey[300],
    boxShadow:
      "rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
    "& .MuiMenu-list": { padding: "4px 0" },
    "& .MuiMenuItem-root": {
      "& .MuiSvgIcon-root": {
        fontSize: 18,
        color: theme.palette.text.secondary,
        marginRight: theme.spacing(1.5),
      },
      "&:active": {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity
        ),
      },
    },
  },
}));

export default function StockLedgerReport() {
  const edService = new EncryptDecryptService();
  const navigate = useNavigate();
  const location = useLocation();

  function useQuery() {
    const { search } = useLocation();
    return React.useMemo(() => new URLSearchParams(search), [search]);
  }
  const urlParams = useQuery();
  const state = location.state as any;

  const productIdFromState = state?.productId || 0;
  const productIdsFromState = state?.productIds || [];
  const startDateFromState = state?.startDate || "";
  const endDateFromState = state?.endDate || "";

  const productIdFromLegacyList =
    Array.isArray(productIdsFromState) && productIdsFromState.length > 0
      ? Number(productIdsFromState[0]) || 0
      : 0;
  const productId = productIdFromState || productIdFromLegacyList || 0;

  const productIdFromPlainUrl = Number(urlParams.get("productId")) || 0;
  const startDateFromPlainUrl = urlParams.get("startDate") || "";
  const endDateFromPlainUrl = urlParams.get("endDate") || "";
  const enledgerAccountId: any = urlParams.get("q");
  const productIdFromEncryptedUrl = enledgerAccountId
    ? parseInt(edService.decrypt(enledgerAccountId) || "0")
    : 0;

  const finalProductId =
    productId || productIdFromPlainUrl || productIdFromEncryptedUrl || 0;
  const startDateFromNav = startDateFromState || startDateFromPlainUrl;
  const endDateFromNav = endDateFromState || endDateFromPlainUrl;

  const { userContext, updateUserContext } = useContext(PreferencesContext);

  const [sourceProductCollection, setSourceProductCollection] = useState<
    IProduct[]
  >([]);
  const [warehouseCollection, setWarehouseCollection] = useState<any[]>([]);
  const [categoryCollection, setCategoryCollection] = useState<any[]>([]);

  const [productName, setProductName] = useState<string>("");
  const [selectedWarehouses, setSelectedWarehouses] = useState<any[]>([]);
  const [selectedWarehouseIds, setSelectedWarehouseIds] = useState<string[]>(
    []
  );
  const [selectedCategories, setSelectedCategories] = useState<any[]>([]);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState<string[]>([]);
  const [selectedVoucherTypes, setSelectedVoucherTypes] = useState<any[]>([]);
  const [selectedVoucherTypeCodes, setSelectedVoucherTypeCodes] = useState<
    string[]
  >([]);
  const [valuationMethod, setValuationMethod] =
    useState<string>("WEIGHTED_AVERAGE");

  const [isLoading, setLoading] = useState(false);
  const [anchorEl2, setAnchorEl2] = useState<null | HTMLElement>(null);
  const openExportType = Boolean(anchorEl2);
  const [selectedRowIndex, setSelectedRowIndex] = useState(-1);

  const [allGroups, setAllGroups] = useState<StockLedgerProductGroup[]>([]);
  const [rawLedgerRows, setRawLedgerRows] = useState<any[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>("");

  const [fromDate, setFromDate] = useState<any>(() => {
    if (startDateFromNav) return new Date(startDateFromNav);
    return userContext.financialYearStartDate;
  });
  const [toDate, setToDate] = useState<any>(() => {
    if (endDateFromNav) return new Date(endDateFromNav);
    return userContext.financialYearEndDate;
  });

  const selectedProductId = Number(finalProductId || 0);
  const isProductSelected = selectedProductId > 0;

  const productData = useMemo(() => allGroups[0] || null, [allGroups]);
  const displayName =
    productName || productData?.productName || "Product";

  const handleClickExportType = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl2(event.currentTarget);
  };
  const handleCloseExportType = () => setAnchorEl2(null);

  const handleWarehouseChange = (object: any) => {
    setSelectedWarehouses(object || []);
    const ids: string[] = [];
    (object || []).forEach((x: any) => {
      if (x?.value === "*") return;
      const id = x?.warehouseId ?? x?.value;
      if (id === undefined || id === null || id === "") return;
      ids.push(String(id));
    });
    const allSelected =
      warehouseCollection.length > 0 &&
      ids.length >= warehouseCollection.length;
    setSelectedWarehouseIds(allSelected ? [] : ids);
  };

  const handleCategoryChange = (object: any) => {
    setSelectedCategories(object || []);
    const ids: string[] = [];
    (object || []).forEach((x: any) => {
      if (x?.value === "*") return;
      const id = x?.categoryId ?? x?.value;
      if (id === undefined || id === null || id === "") return;
      ids.push(String(id));
    });
    const allSelected =
      categoryCollection.length > 0 && ids.length >= categoryCollection.length;
    setSelectedCategoryIds(allSelected ? [] : ids);
  };

  const handleVoucherTypeChange = (object: any) => {
    setSelectedVoucherTypes(object || []);
    const codes: string[] = [];
    (object || []).forEach((x: any) => {
      if (x?.value === "*") return;
      if (x?.value != null && x.value !== "") codes.push(String(x.value));
    });
    const allSelected =
      VOUCHER_TYPE_OPTIONS.length > 0 &&
      codes.length >= VOUCHER_TYPE_OPTIONS.length;
    setSelectedVoucherTypeCodes(allSelected ? [] : codes);
  };

  async function getProducts(searchValue: string) {
    const params = new URLSearchParams();
    params.append("consumerId", userContext.consumerId.toString());
    params.append("firmId", userContext.firmId.toString());
    params.append("branchId", userContext.branchId.toString());
    params.append("pageIndex", "1");
    params.append("pageSize", "1000");
    params.append("sortColumn", "name");
    params.append("sortDirection", "ASC");
    params.append("searchValue", searchValue || "");
    params.append("mode", "");
    params.append("financialYear", userContext.financialYear?.toString());
    try {
      const result: any = await GetProductList(params);
      const productCollectionData = result?.data?.data?.productCollection;
      if (productCollectionData) {
        const formattedData = productCollectionData.map((item: any) => ({
          ...item,
          label: item.name,
          value: item.productId.toString(),
          categoryId: Number(item.categoryId ?? item.CategoryId ?? 0),
        }));
        setSourceProductCollection(formattedData);
        return formattedData;
      }
    } catch (error) {
      console.log(error);
    }
    return [];
  }

  async function fetchWarehouses() {
    try {
      const params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId.toString());
      params.append("firmId", userContext.firmId.toString());
      params.append("branchId", userContext.branchId.toString());
      params.append("pageIndex", "1");
      params.append("pageSize", "1000");
      params.append("sortColumn", "name");
      params.append("sortDirection", "ASC");
      params.append("searchValue", "");
      params.append("mode", "");
      const result: any = await GetWarehouseList(params);
      const list =
        result?.data?.data?.warehouseCollection ||
        result?.data?.warehouseCollection ||
        [];
      setWarehouseCollection(
        (list || []).map((w: any) => ({
          ...w,
          label: w.name || w.warehouseName || String(w.warehouseId),
          value: String(w.warehouseId),
          warehouseId: w.warehouseId,
        }))
      );
    } catch (e: any) {
      console.log(e);
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage: e?.message || "Failed to load godowns",
        alertType: AlertType.Error,
      });
    }
  }

  async function fetchCategories() {
    try {
      const params = new URLSearchParams();
      params.append("consumerId", userContext.consumerId.toString());
      params.append("firmId", userContext.firmId.toString());
      params.append("branchId", userContext.branchId.toString());
      params.append("pageIndex", "1");
      params.append("pageSize", "1000");
      params.append("sortColumn", "name");
      params.append("sortDirection", "ASC");
      params.append("searchValue", "");
      params.append("mode", "");
      const result: any = await GetCategoryList(params);
      const list =
        result?.data?.data?.categoryCollection ||
        result?.data?.categoryCollection ||
        [];
      setCategoryCollection(
        (list || []).map((c: any) => ({
          ...c,
          label: c.name || c.categoryName || String(c.categoryId),
          value: String(c.categoryId),
          categoryId: c.categoryId,
        }))
      );
    } catch (e: any) {
      console.log(e);
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage: e?.message || "Failed to load categories",
        alertType: AlertType.Error,
      });
    }
  }

  const rebuildGroupsFromRaw = (
    rows: any[],
    productIdList: string[],
    products: IProduct[] = sourceProductCollection
  ) => {
    const selectedIds = productIdList.map((id) => Number(id));
    const selectedProducts = products
      .filter((p) => selectedIds.includes(Number(p.productId)))
      .map((p) => ({
        productId: Number(p.productId),
        name: p.name,
        categoryId: Number(p.categoryId || 0),
      }));

    if (!selectedProducts.length) {
      selectedIds.forEach((id) =>
        selectedProducts.push({
          productId: id,
          name: productName || String(id),
          categoryId: 0,
        })
      );
    }

    const warehouseNameById = new Map<string, string>();
    (warehouseCollection || []).forEach((w: any) => {
      const id = String(w.warehouseId ?? w.value ?? "");
      const name = w.name || w.label || w.warehouseName || "";
      if (id && name) warehouseNameById.set(id, name);
    });

    const from = fromDate ? new Date(fromDate) : null;
    const to = toDate ? new Date(toDate) : null;
    if (from) from.setHours(0, 0, 0, 0);
    if (to) to.setHours(23, 59, 59, 999);

    const enrichedRows = (rows || [])
      .map((item: any) => {
        const wid = Number(
          item.WarehouseId ?? item.warehouseId ?? item.warehouse_id ?? 0
        );
        const existingName =
          item.WarehouseName ?? item.warehouseName ?? item.warehouse_name ?? "";
        if (wid > 0 && !existingName && warehouseNameById.has(String(wid))) {
          return {
            ...item,
            WarehouseName: warehouseNameById.get(String(wid)),
            warehouseName: warehouseNameById.get(String(wid)),
          };
        }
        return item;
      })
      .filter((item: any) => {
        const source = String(
          item.SourceType ?? item.sourceType ?? item.source_type ?? ""
        ).toUpperCase();
        const vType = String(
          item.VoucherType ?? item.voucherType ?? item.voucher_type ?? ""
        ).toUpperCase();
        const rowType = String(
          item.RowType ?? item.rowType ?? item.row_type ?? ""
        ).toUpperCase();
        const isOpening =
          rowType === "OPENING" ||
          source === "OPENING" ||
          vType === "OPENING";
        if (isOpening) return true;
        if (!from || !to) return true;
        const voucherDate =
          item.VoucherDate ?? item.voucherDate ?? item.voucher_date;
        if (!voucherDate) return true;
        const d = new Date(voucherDate);
        return d >= from && d <= to;
      });

    const groups = buildProductGroups(enrichedRows, selectedProducts, {
      warehouseIds: selectedWarehouseIds,
      categoryIds: selectedCategoryIds,
      voucherTypes: selectedVoucherTypeCodes,
      valuationMethod,
    });

    groups.forEach((g) => {
      g.details.forEach((d) => {
        if (
          d.rowType === "TXN" &&
          d.warehouseId > 0 &&
          !d.warehouseName &&
          warehouseNameById.has(String(d.warehouseId))
        ) {
          d.warehouseName = warehouseNameById.get(String(d.warehouseId)) || "";
        }
      });
    });

    setAllGroups(groups);
    return groups;
  };

  const fetchStockLedgerReportList = async () => {
    const productIdVal = selectedProductId;
    if (!productIdVal) {
      setAllGroups([]);
      return;
    }

    const productIdList = [String(productIdVal)];
    setLoading(true);
    setErrorMessage("");

    try {
      const params = new URLSearchParams();
      params.append("consumer_id", userContext.consumerId.toString());
      params.append("firm_id", userContext.firmId.toString());
      params.append("branch_id", userContext.branchId.toString());
      params.append("pageIndex", "1");
      params.append("pageSize", "9999");
      params.append("financialYear", userContext.financialYear.toString());
      params.append("product_id", String(productIdVal));
      params.append("batch_number", "");
      params.append("searchValue", "");
      params.append("start_date", format(new Date(fromDate), "yyyy-MM-dd"));
      params.append("end_date", format(new Date(toDate), "yyyy-MM-dd"));
      params.append("valuation_method", valuationMethod);
      if (selectedWarehouseIds.length) {
        params.append("warehouse_ids", selectedWarehouseIds.join(","));
      }
      if (selectedCategoryIds.length) {
        params.append("category_ids", selectedCategoryIds.join(","));
      }
      if (selectedVoucherTypeCodes.length) {
        params.append("voucher_types", selectedVoucherTypeCodes.join(","));
      }

      const result = await GetStockLedgerList(params.toString());

      const dataRoot = result?.data?.data || result?.data || result;
      const allStockData =
        dataRoot?.StockLedgerReportCollection ||
        dataRoot?.stockLedgerReportCollection ||
        [];

      setRawLedgerRows(allStockData);
      const groups = rebuildGroupsFromRaw(allStockData, productIdList);
      if (groups[0]?.productName) {
        setProductName(groups[0].productName);
      }
    } catch (error: any) {
      setAllGroups([]);
      setErrorMessage(error?.message || "Failed to fetch stock ledger");
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage: error?.message || "Failed to fetch data",
        alertType: AlertType.Error,
      });
    } finally {
      setLoading(false);
    }
  };

  const voucherRedirect = (row: StockLedgerDetailRow) => {
    if (row.rowType !== "TXN") return;
    const voucherId = Number(row.voucherId || 0);
    if (!voucherId) {
      updateUserContext({
        ...userContext,
        isAlert: true,
        alertMessage: "Voucher link not available for this entry.",
        alertType: AlertType.Error,
      });
      return;
    }
    const mode = String(row.sourceType || row.voucherType || "")
      .toUpperCase()
      .replace(/\s+/g, "")
      .replace(/_/g, "");
    const enId = edService.encrypt(voucherId);
    switch (mode) {
      case "SALE":
        navigate({
          pathname: "/invoice-save",
          search: `?q=${encodeURIComponent(enId)}&t=${
            userContext.enableCompositionTax ? "composition" : "tax"
          }-invoice`,
        });
        break;
      case "PURCHASE":
        navigate({
          pathname: "/purchase-save",
          search: `?q=${encodeURIComponent(enId)}`,
        });
        break;
      case "CREDITNOTE":
      case "SALESRETURN":
        navigate({
          pathname: "/credit-note-save",
          search: `?q=${encodeURIComponent(enId)}`,
        });
        break;
      case "DEBITNOTE":
      case "PURCHASERETURN":
        navigate({
          pathname: "/debit-note-save",
          search: `?q=${encodeURIComponent(enId)}`,
        });
        break;
      case "STOCKTRANSFER":
        navigate({
          pathname: "/stock-transfer-save",
          search: `?q=${encodeURIComponent(enId)}`,
        });
        break;
      case "STOCKADJUSTMENT":
      case "ADJUSTMENT":
        navigate({
          pathname: "/stock-adjustment-save",
          search: `?q=${encodeURIComponent(enId)}`,
        });
        break;
      default:
        updateUserContext({
          ...userContext,
          isAlert: true,
          alertMessage: `Navigation not configured for voucher type: ${row.voucherType || row.sourceType}`,
          alertType: AlertType.Error,
        });
        break;
    }
  };

  const handleClientExport = (type: "csv" | "excel" | "pdf") => {
    handleCloseExportType();
    const meta = {
      companyName: userContext.firmName || userContext.consumerName || "Company",
      periodFrom: format(new Date(fromDate), "dd-MM-yyyy"),
      periodTo: format(new Date(toDate), "dd-MM-yyyy"),
      valuation:
        VALUATION_OPTIONS.find((v) => v.value === valuationMethod)?.label ||
        valuationMethod,
    };
    if (type === "pdf") {
      printLedger(allGroups, meta);
      return;
    }
    exportLedgerToCsvOrExcel(allGroups, { ...meta, type });
  };

  useEffect(() => {
    getProducts("");
    fetchWarehouses();
    fetchCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (finalProductId > 0 && sourceProductCollection.length > 0) {
      const product = sourceProductCollection.find(
        (p) => Number(p.productId) === Number(finalProductId)
      );
      if (product) {
        setProductName(product.name || product.label || "");
      }
    }
    if (startDateFromNav && endDateFromNav) {
      setFromDate(new Date(startDateFromNav));
      setToDate(new Date(endDateFromNav));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [finalProductId, sourceProductCollection]);

  useEffect(() => {
    if (selectedProductId > 0) {
      fetchStockLedgerReportList();
    } else {
      setAllGroups([]);
      setRawLedgerRows([]);
      setProductName("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fromDate, toDate, selectedProductId]);

  useEffect(() => {
    if (!rawLedgerRows.length || !selectedProductId) return;
    rebuildGroupsFromRaw(rawLedgerRows, [String(selectedProductId)]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedWarehouseIds,
    selectedCategoryIds,
    selectedVoucherTypeCodes,
    valuationMethod,
    warehouseCollection,
  ]);

  const handleBack = () => navigate(-1);

  // Match LedgerDataTable desktop cell / header sizing
  const cellSx = {
    fontSize: 14,
    py: 1.25,
    px: 1.5,
    verticalAlign: "top" as const,
  };

  const headerSx = {
    ...cellSx,
    // Ledger header looks taller because of checkbox; without it we need explicit height
    py: "12px !important",
    height: 48,
    minHeight: 48,
    fontWeight: 700,
    fontSize: 13,
    color: LV_COLORS.text,
    bgcolor: "#f5f5f5 !important",
    whiteSpace: "nowrap" as const,
    borderBottom: "none",
    verticalAlign: "middle" as const,
  };

  const selectMenuProps = {
    menuPortalTarget: typeof document !== "undefined" ? document.body : null,
    menuPosition: "fixed" as const,
    menuPlacement: "bottom" as const,
    styles: {
      menuPortal: (base: any) => ({ ...base, zIndex: 9999 }),
      menu: (base: any) => ({ ...base, zIndex: 9999 }),
    },
  };

  const renderQty = (value: number, emphasizeNegative = false, bold = false) => {
    const n = Number(value || 0);
    const isNeg = emphasizeNegative && n < 0;
    return (
      <TableCell
        align="right"
        sx={{
          ...cellSx,
          whiteSpace: "nowrap",
          color: isNeg ? LV_COLORS.debit : LV_COLORS.text,
          fontWeight: bold || isNeg ? 700 : 400,
        }}
      >
        {n === 0 && !bold ? "" : qtyFmt(n)}
      </TableCell>
    );
  };

  const renderAmt = (value: number, bold = false, hideZero = false) => {
    const n = Number(value || 0);
    return (
      <TableCell
        align="right"
        sx={{
          ...cellSx,
          whiteSpace: "nowrap",
          fontWeight: bold ? 700 : 400,
          color: LV_COLORS.text,
        }}
      >
        {hideZero && n === 0 ? "" : amtFmt(n)}
      </TableCell>
    );
  };

  return (
    <>
      <Backdrop
        sx={{
          color: "#fff",
          backgroundColor: "#fff",
          zIndex: (t) => t.zIndex.drawer + 1,
        }}
        open={isLoading}
      >
        <SplashComponent />
      </Backdrop>
      <Title pageTitle="Stock Ledger" />
      <Box
        className="boxContainer"
        sx={{
          "& > :not(style)": { width: "100%" },
        }}
      >
        {userContext.consumerId > 0 ? (
          <Grid>
            {/* HEADER - same markup/spacing as LedgerVoucherHeader */}
            <Box sx={{ width: "100%" }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "wrap",
                  gap: 1,
                  mt: 0.5,
                  mb: 0.25,
                  width: "100%",
                }}
              >
                <Box sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    sx={{
                      fontSize: 19,
                      fontWeight: 600,
                      color: LV_COLORS.primary,
                      lineHeight: 1.3,
                      wordBreak: "break-word",
                    }}
                  >
                    Stock Ledger
                    {isProductSelected ? ` - ${displayName}` : ""}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                    flexShrink: 0,
                  }}
                >
                  <LoadingButton
                    variant="outlined"
                    onClick={handleBack}
                    sx={{ textTransform: "none" }}
                  >
                    <ShortcutButton label="ESC" />
                    Back
                  </LoadingButton>
                </Box>
              </Box>
            </Box>

            {/* PAPER SECTION - same shell as Ledger Voucher */}
            <Grid
              className="paper top-space"
              sx={{ width: "100%", pt: "6px !important" }}
            >
              <Paper
                elevation={0}
                className="paper-container"
                sx={{
                  overflow: "hidden",
                  border: `1px solid ${LV_COLORS.border}`,
                  borderRadius: "12px !important",
                  bgcolor: LV_COLORS.card,
                  px: { xs: 1.5, md: 2 },
                  py: { xs: 1, md: 1.25 },
                }}
              >
                {/* Toolbar */}
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: { xs: "column", md: "row" },
                    flexWrap: "wrap",
                    alignItems: { xs: "stretch", md: "center" },
                    justifyContent: "space-between",
                    gap: 1,
                    width: "100%",
                    pb: 1,
                    mb: 0.5,
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      gap: 1,
                      flex: "1 1 auto",
                      minWidth: 0,
                    }}
                  >
                    <Box sx={{ width: { xs: "100%", sm: 200 }, minWidth: 0 }}>
                      <MultiSelectSim
                        key="categoryId"
                        placeholder="Category..."
                        options={categoryCollection}
                        onChange={handleCategoryChange}
                        value={selectedCategories}
                        isSelectAll={true}
                        isDisabled={!isProductSelected}
                        {...selectMenuProps}
                      />
                    </Box>
                    <Box sx={{ width: { xs: "100%", sm: 200 }, minWidth: 0 }}>
                      <MultiSelectSim
                        key="warehouseId"
                        placeholder="Warehouse..."
                        options={warehouseCollection}
                        onChange={handleWarehouseChange}
                        value={selectedWarehouses}
                        isSelectAll={true}
                        isDisabled={!isProductSelected}
                        {...selectMenuProps}
                      />
                    </Box>
                    <Box sx={{ width: { xs: "100%", sm: 200 }, minWidth: 0 }}>
                      <MultiSelectSim
                        key="voucherType"
                        placeholder="Voucher type..."
                        options={VOUCHER_TYPE_OPTIONS}
                        onChange={handleVoucherTypeChange}
                        value={selectedVoucherTypes}
                        isSelectAll={true}
                        isDisabled={!isProductSelected}
                        {...selectMenuProps}
                      />
                    </Box>
                    <Box sx={{ width: { xs: "100%", sm: 180 }, minWidth: 0 }}>
                      <TextField
                        select
                        fullWidth
                        size="small"
                        value={valuationMethod}
                        onChange={(e) => setValuationMethod(e.target.value)}
                        disabled={!isProductSelected}
                        sx={{
                          "& .MuiOutlinedInput-root": {
                            height: 40,
                            bgcolor: LV_COLORS.card,
                          },
                        }}
                      >
                        {VALUATION_OPTIONS.map((opt) => (
                          <MenuItem key={opt.value} value={opt.value}>
                            {opt.label}
                          </MenuItem>
                        ))}
                      </TextField>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: "flex",
                      flexWrap: "wrap",
                      alignItems: "center",
                      justifyContent: { xs: "stretch", md: "flex-end" },
                      gap: 1,
                      flex: "0 0 auto",
                    }}
                  >
                    <CustomDateRangePicker
                      fromDate={fromDate}
                      setFromDate={setFromDate}
                      toDate={toDate}
                      setToDate={setToDate}
                      disabled={!isProductSelected}
                    />
                    <Tooltip title="Print">
                      <span>
                        <IconButton
                          color="primary"
                          aria-label="Print stock ledger"
                          disabled={!isProductSelected || !allGroups.length}
                          onClick={() => handleClientExport("pdf")}
                          sx={{ height: 40, width: 40 }}
                        >
                          <LocalPrintshopIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Button
                      variant="outlined"
                      disableElevation
                      disabled={!isProductSelected || !allGroups.length}
                      onClick={handleClickExportType}
                      startIcon={<DownloadIcon />}
                      endIcon={<KeyboardArrowDownSharp />}
                      sx={{
                        textTransform: "none",
                        height: 40,
                        width: { xs: "100%", md: "auto" },
                      }}
                    >
                      Export
                    </Button>
                    <StyledMenu
                      id="stock-ledger-export"
                      anchorEl={anchorEl2}
                      open={openExportType}
                      onClose={handleCloseExportType}
                    >
                      <MenuItem
                        disableRipple
                        onClick={() => handleClientExport("pdf")}
                      >
                        PDF
                      </MenuItem>
                      <MenuItem
                        disableRipple
                        onClick={() => handleClientExport("excel")}
                      >
                        EXCEL
                      </MenuItem>
                      <MenuItem
                        disableRipple
                        onClick={() => handleClientExport("csv")}
                      >
                        CSV
                      </MenuItem>
                    </StyledMenu>
                  </Box>
                </Box>

                {/* Body */}
                {!isProductSelected ? (
                  <Box
                    sx={{
                      py: 6,
                      textAlign: "center",
                      color: LV_COLORS.textSecondary,
                    }}
                  >
                    Open Stock Ledger from Stock or Products list.
                  </Box>
                ) : errorMessage ? (
                  <Box
                    sx={{
                      py: 4,
                      textAlign: "center",
                      color: LV_COLORS.debit,
                      backgroundColor: LV_COLORS.badgeRed,
                      borderRadius: 1,
                    }}
                  >
                    {errorMessage}
                  </Box>
                ) : !productData ? (
                  <Box
                    sx={{
                      py: 6,
                      textAlign: "center",
                      color: LV_COLORS.textSecondary,
                    }}
                  >
                    No transactions found for selected period
                  </Box>
                ) : (
                  <>
                    <TableContainer
                      sx={{
                        width: "100%",
                        overflowX: "auto",
                        borderRadius: "8px",
                        border: `1px solid ${LV_COLORS.border}`,
                      }}
                    >
                      <Table
                        stickyHeader
                        size="medium"
                        sx={{ width: "100%", minWidth: 1100 }}
                      >
                        <TableHead
                          className="table-header-color"
                          sx={{
                            "& .MuiTableCell-root": {
                              borderBottom: "none !important",
                              borderTop: "none !important",
                              bgcolor: "#f5f5f5 !important",
                              py: "12px !important",
                              height: "48px !important",
                              minHeight: "48px !important",
                              boxSizing: "border-box",
                            },
                          }}
                        >
                          <TableRow>
                            <TableCell sx={{ ...headerSx, minWidth: 110 }}>
                              Date
                            </TableCell>
                            <TableCell sx={{ ...headerSx, minWidth: 120 }}>
                              Voucher No.
                            </TableCell>
                            <TableCell sx={{ ...headerSx, minWidth: 110 }}>
                              Type
                            </TableCell>
                            <TableCell sx={{ ...headerSx, minWidth: 160 }}>
                              Party / Particulars
                            </TableCell>
                            <TableCell sx={{ ...headerSx, minWidth: 110 }}>
                              Warehouse
                            </TableCell>
                            <TableCell
                              sx={{ ...headerSx, minWidth: 100 }}
                              align="right"
                            >
                              Inward Qty
                            </TableCell>
                            <TableCell
                              sx={{ ...headerSx, minWidth: 100 }}
                              align="right"
                            >
                              Outward Qty
                            </TableCell>
                            <TableCell
                              sx={{ ...headerSx, minWidth: 90 }}
                              align="right"
                            >
                              Rate (₹)
                            </TableCell>
                            <TableCell
                              sx={{ ...headerSx, minWidth: 110 }}
                              align="right"
                            >
                              Balance Qty
                            </TableCell>
                            <TableCell
                              sx={{ ...headerSx, minWidth: 120 }}
                              align="right"
                            >
                              Stock Value (₹)
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {productData.details.map((detailData, detailIndex) => {
                            const isSummary = detailData.rowType !== "TXN";
                            const isOpening = detailData.rowType === "OPENING";
                            const isTotal = detailData.rowType === "TOTAL";
                            const isClosing = detailData.rowType === "CLOSING";
                            const selected = selectedRowIndex === detailIndex;

                            const dateLabel = isOpening
                              ? "Opening Balance"
                              : isTotal
                              ? "Total"
                              : isClosing
                              ? "Closing Balance"
                              : formatLedgerDate(detailData.voucherDate);

                            return (
                              <TableRow
                                key={`${productData.productId}-${detailIndex}`}
                                hover={!isSummary}
                                selected={selected}
                                onClick={() =>
                                  !isSummary && setSelectedRowIndex(detailIndex)
                                }
                                sx={{
                                  cursor: isSummary ? "default" : "pointer",
                                  ...(selected && !isSummary
                                    ? {
                                        backgroundColor: `${LV_COLORS.selectedRow} !important`,
                                        "& .MuiTableCell-root": {
                                          backgroundColor: `${LV_COLORS.selectedRow} !important`,
                                        },
                                      }
                                    : {}),
                                  "&:hover .MuiTableCell-root": !isSummary
                                    ? { bgcolor: "#FAFBFF" }
                                    : {},
                                }}
                              >
                                <TableCell
                                  sx={{
                                    ...cellSx,
                                    fontWeight: isSummary ? 700 : 400,
                                    whiteSpace: "nowrap",
                                  }}
                                >
                                  {dateLabel}
                                </TableCell>
                                <TableCell sx={{ ...cellSx, whiteSpace: "nowrap" }}>
                                  {detailData.rowType === "TXN" &&
                                  detailData.voucherNo ? (
                                    <Button
                                      disableRipple
                                      color="inherit"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        voucherRedirect(detailData);
                                      }}
                                      className="manuButton-link"
                                      style={{
                                        backgroundColor: "transparent",
                                        padding: 0,
                                        minWidth: 0,
                                      }}
                                    >
                                      <span className="text-link">
                                        {detailData.voucherNo}
                                      </span>
                                    </Button>
                                  ) : (
                                    detailData.voucherNo || ""
                                  )}
                                </TableCell>
                                <TableCell
                                  sx={{
                                    ...cellSx,
                                    whiteSpace: "nowrap",
                                    color: LV_COLORS.text,
                                  }}
                                >
                                  {isSummary ? "" : detailData.voucherType || ""}
                                </TableCell>
                                <TableCell sx={cellSx}>
                                  <Typography
                                    component="span"
                                    sx={{
                                      fontSize: 14,
                                      fontWeight: isSummary ? 700 : 600,
                                      color: LV_COLORS.text,
                                    }}
                                  >
                                    {detailData.partyName || ""}
                                  </Typography>
                                </TableCell>
                                <TableCell sx={{ ...cellSx, whiteSpace: "nowrap" }}>
                                  {isSummary
                                    ? ""
                                    : detailData.warehouseName || ""}
                                </TableCell>
                                {renderQty(
                                  detailData.inwardQty,
                                  false,
                                  isTotal
                                )}
                                {renderQty(
                                  detailData.outwardQty,
                                  false,
                                  isTotal
                                )}
                                {renderAmt(
                                  isTotal ? 0 : detailData.rate,
                                  isSummary,
                                  isTotal
                                )}
                                {renderQty(
                                  detailData.closingBalance,
                                  true,
                                  isSummary
                                )}
                                {renderAmt(detailData.stockValue, isSummary)}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </Paper>
            </Grid>
          </Grid>
        ) : (
          <MissingConsumer consumerId={userContext.consumerId} />
        )}
      </Box>
    </>
  );
}
